import WeatherApi from '@/api/config/weatherApi'
import { RootState } from '@/store/stores'
import { SearchParams } from '@/types'
import {
  TelemetryWeatherReportParams,
  WeatherReportParams,
} from '@/types/weather'
import { isWeatherStation } from '@/utils/assetType'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  initialState,
  WeatherBarometerData,
  WeatherReportData,
  WeatherReportFields,
  getColumnDetails,
  AssetData,
} from '@store/slices/WeatherDashboardV2/types'
import { AssetType } from '@/types/telemetry'
export const fetchAssetData = createAsyncThunk(
  'weatherDashboard/fetchAssetData',
  async (
    params: {
      EntityId: number
      searchParams: SearchParams
    },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await WeatherApi.getAssetList(params.EntityId, {
        ...params.searchParams,
      })
      return {
        assetData: data?.data as AssetData[],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchTagKeyData = createAsyncThunk(
  'weatherDashboard/fetchTagKeyData',
  async (
    params: {
      AssetId: number
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await WeatherApi.getTagKey(params.AssetId)
      return {
        weatherStationId: params.AssetId,
        tagKeys: data as unknown as string[],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

import { staticConvertToMetric } from '@/hooks/useUnits'

export const fetchLatestWeatherReport = createAsyncThunk(
  'weatherDashboard/fetchLatestWeatherReport',
  async (params: TelemetryWeatherReportParams, { rejectWithValue }) => {
    try {
      const { data } = await WeatherApi.getWeatherReportV2(params)

      const weatherData: WeatherReportData[] = (data ?? []).map((item: any) => {
        const result: any = {
          timeSpan: item.time,
        }

        const fields = Object.keys(WeatherReportFields) as Array<
          keyof typeof WeatherReportFields
        >

        fields.forEach((key) => {
          const column = WeatherReportFields[key].columnName
          const rawValue = item[column]

          result[column] = rawValue
          // convertValueToMetric(rawValue, column)
        })

        return result as WeatherReportData
      })

      return {
        weatherStationId: params.AssetId,
        weatherData,
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetch30DaysData = createAsyncThunk(
  'weatherDashboard/fetch24hData',
  async (params: TelemetryWeatherReportParams, { rejectWithValue }) => {
    try {
      const { data } = await WeatherApi.getWeatherReportV2(params)

      const weatherData = (data ?? []).map((item: any) => ({
        timeSpan: item.time,
        rainfall_mm: convertValueToMetric(
          item.rainfall_mm,
          WeatherReportFields.RainFall.columnName,
        ),
        temp_out: convertValueToMetric(
          item.temp_out,
          WeatherReportFields.TemperatureOutdoor.columnName,
        ),
        et: convertValueToMetric(
          item.et,
          WeatherReportFields.Evapotranspiration.columnName,
        ),
        lightning_strikes: convertValueToMetric(
          item.lightning_strikes,
          WeatherReportFields.LightningStrikeCount.columnName,
        ),
      })) as WeatherReportData[]
      return { weatherStationId: params.AssetId, weatherData: weatherData }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

function convertValueToMetric(value: number, columnName: string): number {
  return value
  // const needConversion = [
  //   WeatherReportFields.TemperatureOutdoor.columnName.toLowerCase(),
  //   WeatherReportFields.Evapotranspiration.columnName.toLowerCase(),
  //   WeatherReportFields.HumidityOutdoor.columnName.toLowerCase(),
  //   WeatherReportFields.RainFall.columnName.toLowerCase(),
  //   WeatherReportFields.TemperatureOutdoor.columnName.toLowerCase(),
  // ]
  // const columnDetail = getColumnDetails(columnName)
  // if (!needConversion.includes(columnName.toLowerCase()) || !columnDetail) {
  //   return value
  // }

  // return staticConvertToMetric(
  //   value,
  //   columnDetail.metricUnit,
  //   columnDetail.imperialUnit,
  // )
}

export const fetchBarometerData = createAsyncThunk(
  'weatherDashboard/fetchBarometerData',
  async (params: TelemetryWeatherReportParams, { rejectWithValue }) => {
    try {
      const { data } = await WeatherApi.getWeatherReportV2(params)

      const weatherData = (data ?? []).map((item: any) => ({
        timeSpan: item.time,
        value: convertValueToMetric(
          item.pressure_last,
          WeatherReportFields.PressureLastReading.columnName,
        ),
      })) as WeatherBarometerData[]
      return { weatherStationId: params.AssetId, barometerData: weatherData }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const weatherDashboardSliceV2 = createSlice({
  name: 'diagnosticMapView',
  initialState,
  reducers: {
    setAssetId: (state, action: PayloadAction<{ assetId: number }>) => {
      state.assetId = action.payload.assetId
    },
    setAssetData: (state, action: PayloadAction<AssetData[]>) => {
      const assetData = action.payload

      const uniqueTypes = [
        ...new Set(
          assetData.map((item) => item.metadata?.type || AssetType.Other),
        ),
      ]

      const sortedTypes = uniqueTypes.sort((a, b) => {
        const aIsOther = a === AssetType.Other
        const bIsOther = b === AssetType.Other
        if (aIsOther && !bIsOther) return 1
        if (bIsOther && !aIsOther) return -1
        return a.localeCompare(b)
      })

      const sensorTypes = sortedTypes.map((name, index) => ({
        id: index + 1,
        name,
      }))

      const otherTypeId =
        sensorTypes.find((t) => t.name === AssetType.Other)?.id ?? null

      const assetDataWithType = assetData.map((asset) => {
        const typeName = asset.metadata?.type || AssetType.Other
        const matchedType = sensorTypes.find((t) => t.name === typeName)

        return {
          ...asset,
          sensorTypeId: matchedType?.id ?? otherTypeId,
        }
      })

      const weatherStations = assetDataWithType.filter(
        (item) => isWeatherStation(item),
      )

      state.sensorTypes.isLoading = false
      state.sensorTypes.data = sensorTypes
      state.assetData = assetDataWithType
      state.assetId = weatherStations[0]?.id || null
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAssetData
      .addCase(fetchAssetData.pending, (state) => {
        state.isLoadingAssetData = true
        state.sensorTypes.isLoading = true
      })
      .addCase(fetchAssetData.fulfilled, (state, action) => {
        state.isLoadingAssetData = false

        if (action.payload.assetData) {
          const assetData = action.payload.assetData

          const uniqueTypes = [
            ...new Set(assetData.map((item) => item.metadata?.type || 'Other')),
          ]

          const sortedTypes = uniqueTypes.sort((a, b) => {
            const aIsOther = a === 'Other'
            const bIsOther = b === 'Other'
            if (aIsOther && !bIsOther) return 1
            if (bIsOther && !aIsOther) return -1
            return a.localeCompare(b)
          })

          const sensorTypes = sortedTypes.map((name, index) => ({
            id: index + 1,
            name,
          }))

          const otherTypeId =
            sensorTypes.find((t) => t.name === 'Other')?.id ?? null

          const assetDataWithType = assetData.map((asset) => {
            const typeName = asset.metadata?.type || 'Other'
            const matchedType = sensorTypes.find((t) => t.name === typeName)

            return {
              ...asset,
              sensorTypeId: matchedType?.id ?? otherTypeId,
            }
          })

          const weatherStations = assetDataWithType.filter(
            (item) => isWeatherStation(item),
          )

          state.sensorTypes.isLoading = false
          state.sensorTypes.data = sensorTypes

          state.assetData = assetDataWithType
          state.assetId = weatherStations[0]?.id || null
        }
      })
      .addCase(fetchAssetData.rejected, (state) => {
        state.isLoadingAssetData = false
        state.sensorTypes.isLoading = false
      })
      // fetchTagKey
      .addCase(fetchTagKeyData.pending, (state) => {
        state.isLoadingTagKeys = true
      })
      .addCase(fetchTagKeyData.fulfilled, (state, action) => {
        state.isLoadingTagKeys = false
        if (action.payload.tagKeys) {
          state.tagKeys[`${action.payload.weatherStationId}`] =
            action.payload.tagKeys
        }
      })
      .addCase(fetchTagKeyData.rejected, (state) => {
        state.isLoadingTagKeys = false
      })
      // fetchLatestWeatherReport
      .addCase(fetchLatestWeatherReport.pending, (state) => {
        state.isLoadingLatestWeatherReport = true
      })
      .addCase(fetchLatestWeatherReport.fulfilled, (state, action) => {
        state.isLoadingLatestWeatherReport = false
        if (action.payload.weatherData && action.payload.weatherStationId) {
          state.latestWeatherReport[`${action.payload.weatherStationId}`] =
            action.payload.weatherData[action.payload.weatherData.length - 1]
          state.data24h[`${action.payload.weatherStationId}`] =
            action.payload.weatherData
        }
      })
      .addCase(fetchLatestWeatherReport.rejected, (state) => {
        state.isLoadingLatestWeatherReport = false
      })

      // fetch24hData
      .addCase(fetch30DaysData.pending, (state) => {
        state.isLoading24hTemperature = true
      })
      .addCase(fetch30DaysData.fulfilled, (state, action) => {
        state.isLoading24hTemperature = false
        if (action.payload.weatherData && action.payload.weatherStationId) {
          state.data30d[`${action.payload.weatherStationId}`] =
            action.payload.weatherData
        }
      })
      .addCase(fetch30DaysData.rejected, (state) => {
        state.isLoading24hTemperature = false
      })

      // fetchBarometerData
      .addCase(fetchBarometerData.pending, (state) => {
        state.isLoadingBarometer = true
      })
      .addCase(fetchBarometerData.fulfilled, (state, action) => {
        state.isLoadingBarometer = false
        if (action.payload.barometerData && action.payload.weatherStationId) {
          state.barometerData[`${action.payload.weatherStationId}`] =
            action.payload.barometerData
        }
      })
      .addCase(fetchBarometerData.rejected, (state) => {
        state.isLoadingBarometer = false
      })
  },
})

export const { setAssetId, setAssetData } = weatherDashboardSliceV2.actions

export const weatherDashboardStateV2 = (state: RootState) =>
  state.weatherDashboardV2

export default weatherDashboardSliceV2.reducer
